import React, { useEffect, useState } from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { Alert, Button, Stack, Typography } from "@mui/material";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { getEvents } from "../../api/event.api";
import { getUsers } from "../../api/user.api";
import {
  getEventTournaments,
  addTournament,
  editTournament,
  remove,
} from "../../api/tournament.api.js";
import CreateTournamentModal from "../../components/modals/createTournament.modal.js";
import moment from "moment";
import { setStatus } from "../../api/tournament.api.js";
import { tournamentStatus } from "../../util/tournamentStatus.enum.js";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import DeleteIcon from "@mui/icons-material/Delete";
import StatusChip from "../../components/chips/status.chip.js";
import { useSnackbar } from "../../context/snackbar.context.js";

const AdminTournamentPage = () => {
  const [error, setError] = useState("");
  const [events, setEvents] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [refreshTournaments, setRefreshTournaments] = useState(false);
  const [tournamentToEdit, setTournamentToEdit] = useState();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    if (!selectedEvent) {
      getUsers({"role": 0}),
      getEvents()
        .then((response) => {
          setEvents(response.data);
          if (response.data.length > 0) {
            var event = response.data.find((event) => event.isActive);
            if (event) {
              setSelectedEvent(event.id);
            }
          }
        })
        .catch((e) => {
          setError(e.response.data.reason);
        });
    }

    if (selectedEvent) {
      const fetchData = async () => {
          try {
            const [tournamentResponse, adminResponse] = await Promise.all([
              getEventTournaments(selectedEvent),
              getUsers({"role": "Admin"})
            ]);
      
            setTournaments(tournamentResponse.data);
            setAdmins(adminResponse.data);

          } catch (error) {
            console.error("Fehler beim Laden der Daten:", error);
          }
        };
      
        fetchData();
    }
  }, [refreshTournaments, selectedEvent]);

  const handleSelectedEventChange = (e) => {
    setSelectedEvent(e.target.value);
  };

  const handleSubmit = (formData) => {
    if (!tournamentToEdit) {
      add(selectedEvent, formData);
    } else {
      edit(formData);
    }
    setTournamentToEdit();
  };

  const add = (selectedEvent, tournament) => {
    addTournament(selectedEvent, tournament)
      .then(() => {
        setRefreshTournaments((prev) => !prev);
        showSnackbar("Tournament created!", "success");
      })
      .catch((e) => {
        showSnackbar(
          `Creating Tournament failed! : ${e.response.data.reason}`,
          "error",
        );
        setError(e.response.data.reason);
      });
  };

  const edit = (tournament) => {
    editTournament(tournament)
      .then(() => {
        setRefreshTournaments((prev) => !prev);
        showSnackbar("Tournament edited!", "success");
      })
      .catch((e) => {
        showSnackbar("Unable to edit Tournament!", "error");
        setError(e.response.data.reason);
      });
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setTournamentToEdit();
  };

  const handleEdit = (tournament) => {
    setTournamentToEdit(tournament);
    setOpenModal(true);
  };

  const handleRemove = (id) => {
    remove(id)
      .then(() => {
        setRefreshTournaments((prev) => !prev);
        showSnackbar("Tournament removed!", "success");
      })
      .catch(() => {
        showSnackbar("Unable to remove Tournament!", "error");
      });
  };

  const handleNextStatus = (tournament) => {
    var status = tournamentStatus.find(
      (status) => status.key === tournament.status,
    );
    status = tournamentStatus.find((x) => x.value === status.value + 1);
    if (status) {
      setStatus(tournament.id, status.key)
        .then(() => {
          setRefreshTournaments((prev) => !prev);
          showSnackbar("Tournament is in next status!", "success");
        })
        .catch((e) => {
          setError(e.response.data.reason);
          showSnackbar(
            `Tournament can´t set to next status! - ${e.response.data.reason}`,
            "error",
          );
        });
    }
  };

  return (
    <div className="content-container">
      <div>
        <Typography variant="h3" gutterBottom>
          Tournament Administration
        </Typography>
      </div>
      <div>
        <FormControl fullWidth>
          <InputLabel id="select-event-label">Event</InputLabel>
          <Select
            labelId="select-event-label"
            id="simple-event-select"
            value={selectedEvent}
            label="Event"
            onChange={handleSelectedEventChange}
          >
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Main</TableCell>
                <TableCell>Prize</TableCell>
                <TableCell>Game</TableCell>
                <TableCell>Prtp/M.</TableCell>
                <TableCell>Winr/M.</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>System</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>System</TableCell>
                <TableCell>Part. Mode</TableCell>
                <TableCell>Min/Max Part.</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tournaments.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.name}{" "}
                  </TableCell>
                  <TableCell>{admins.find(admin => admin.id === row.admin)?.nickname}</TableCell>
                  <TableCell>
                    {row.isMainTournament && <MilitaryTechIcon />}
                  </TableCell>
                  <TableCell>{row.prize}</TableCell>
                  <TableCell>{row.game.name}</TableCell>
                  <TableCell>{row.participantsPerMatch}</TableCell>
                  <TableCell>{row.winnerPerMatch}</TableCell>
                  <TableCell sx={{ minWidth: 50, maxWidth: 200 }}>
                    {row.description}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell>{row.system}</TableCell>
                  <TableCell>
                    {moment(row.start).format("DD.MM.YYYY HH:mm")}
                  </TableCell>
                  <TableCell>
                    {moment(row.end).format("DD.MM.YYYY HH:mm")}
                  </TableCell>
                  <TableCell>{row.system}</TableCell>
                  <TableCell>{row.participantMode}</TableCell>
                  <TableCell>
                    {row.participantCountMin} | {row.participantCountMax}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1} direction="row">
                      {(row.status === "Open" ||
                        row.status === "Created" ||
                        row.status === "Started") && (
                        <Button
                          variant="outlined"
                          onClick={() => handleEdit(row)}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        onClick={() => handleNextStatus(row)}
                      >
                        Next Status
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleRemove(row.id)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div>
        {selectedEvent && (
          <Button variant="contained" onClick={handleOpenModal}>
            Create Tournament
          </Button>
        )}
      </div>

      <div>
        <CreateTournamentModal
          aria-modal
          open={openModal}
          handleClose={handleCloseModal}
          handleSubmit={handleSubmit}
          selectedEvent={selectedEvent}
          tournamentData={tournamentToEdit}
          admins={admins}
        />
      </div>

      <div>
        {error && (
          <Alert
            severity="error"
            onClose={() => {
              setError("");
            }}
          >
            {error}
          </Alert>
        )}
      </div>
    </div>
  );
};

export default AdminTournamentPage;
